import { z } from "zod"
import type { User } from "@auth0/auth0-react"
import { isStaffRole } from "~/server/api/utils/roles"

const nameField = (label: string, minLength = 0) =>
  z
    .string()
    .trim()
    .min(minLength, `${label} is required`)
    .max(100)
    .refine((value) => !value.includes("@"), `${label} cannot be an email address`)

export const updateOwnNameSchema = z.object({
  firstName: nameField("First name", 1),
  lastName: nameField("Last name").optional(),
})

/** True if an Auth0 user carries a Hakio staff role (`staff` / legacy `hakio`).
 * Replaces the old email-domain heuristic — Hakio-internal status is now
 * determined by the assigned role, not the email address. */
export function isHakioStaffUser(user: { roles?: { name: string }[] }): boolean {
  return user.roles?.some((role) => isStaffRole(role.name)) ?? false
}

export function isMissingOrEmailLikeName(
  name: string | undefined | null,
  email: string | undefined | null,
): boolean {
  return !name || name === email || name.includes("@")
}

export function needsNameCapture(
  user: Pick<User, "name" | "email" | "given_name" | "family_name"> | undefined,
): boolean {
  if (!user) return false
  return (
    isMissingOrEmailLikeName(user.name, user.email) &&
    !updateOwnNameSchema.safeParse({ firstName: user.given_name, lastName: user.family_name })
      .success
  )
}

export const SYSTEM_ADMIN_NAME = "System Admin"

/** Hakio employees are shown to customers as "System Admin" in activity/publish attribution. */
export function isHakioEmail(email: string | undefined | null): boolean {
  return !!email?.toLowerCase().endsWith("@hakio.com")
}
